import { useMe } from '@/hooks/use-me';
import type { AlertsFilters as Filters } from '@/types/alerts-filters';

import { getEmptyStateData } from './get-empty-state-data';
import { emptySidebarStyles } from './styles';
import { SuggestionDate } from './suggestion-date';
import { SuggestionStatus } from './suggestion-status';
import { SuggestionType } from './suggestion-type';

export type AlertsSidebarEmptyStateProps = {
  isMissing: boolean;
  filters: Filters;
};

export function AlertsSidebarEmptyState({
  isMissing,
  filters,
}: AlertsSidebarEmptyStateProps) {
  const me = useMe();
  const style = emptySidebarStyles();
  const { blindingFilters } = getEmptyStateData(filters, me);

  if (isMissing) {
    return (
      <div className={style.container()}>
        <span role="img" aria-label="sparkles" className={style.icon()}>
          🤔
        </span>
        <div className={style.title()}>We're not tracking this asset yet</div>
        <div>If you need assistance, please contact our support team.</div>
      </div>
    );
  }

  // Check if any filter is actively restricting results
  const noActiveFilters = Object.values(blindingFilters).every((filterGroup) =>
    Object.values(filterGroup).every((value) => value === false),
  );

  if (noActiveFilters) {
    return (
      <div className={style.container()}>
        <span role="img" aria-label="seedling" className={style.icon()}>
          🌱
        </span>
        <div className={style.title()}>Lovely!</div>
        <div>Your work here is complete. All alerts are handled!</div>
      </div>
    );
  }

  return (
    <div className={style.container()}>
      <span role="img" aria-label="sparkles" className={style.icon()}>
        💡
      </span>
      <div className={style.title()}>No alerts found</div>
      <div className="mt-2 max-w-xs text-sm">
        <div className="space-y-2">
          <SuggestionDate blindingFilters={blindingFilters} />
          <SuggestionStatus blindingFilters={blindingFilters} />
          <SuggestionType blindingFilters={blindingFilters} />
        </div>
      </div>
    </div>
  );
}
